import React from 'react';
import statusTypes from '../../status-types';
import { env } from '../../consts/env';
import { executeQuery } from '../../requests/executeQuery';
import { GET_PROPERTY_OWNERSHIPS, GET_OWNERSHIP_INVESTORS } from '../../consts/queries';

const escapeHtml = (value) => {
    if (value == null) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const ownerFetchCache = new Map();

function getGoogleMapsApiKey() {
    const arcConfig = typeof arc !== 'undefined' ? arc : {};
    return arcConfig.google_maps && arcConfig.google_maps.browser_api_key || '';
}

function fetchOwnerInfo(propertyId) {
    if (ownerFetchCache.has(propertyId)) {
        return ownerFetchCache.get(propertyId);
    }
    const promise = executeQuery({
        queryName: GET_PROPERTY_OWNERSHIPS,
        options: {
            param: {
                PROPERTY_ID: String(propertyId),
                limit: '9999',
                offset: '0',
            },
        },
    }).then((ownershipRes) => {
        const ownerships = ownershipRes && ownershipRes.success && Array.isArray(ownershipRes.data) ? ownershipRes.data : [];
        const currentOwnerships = ownerships.filter((o) => o.IS_CURRENT === 1 || o.IS_CURRENT === '1');
        if (currentOwnerships.length === 0) return [];
        return Promise.all(
            currentOwnerships.map((ownership) =>
                executeQuery({
                    queryName: GET_OWNERSHIP_INVESTORS,
                    options: {
                        param: {
                            PROPERTY_OWNERSHIP_ID: String(ownership.ARCIMEDES_ROW_ID),
                            limit: '9999',
                            offset: '0',
                        },
                    },
                })
                    .then((res) => (res && res.success && Array.isArray(res.data) ? res.data : []))
                    .catch(() => [])
            )
        ).then((results) => results.flat());
    }).catch(() => []);
    ownerFetchCache.set(propertyId, promise);
    return promise;
}

function buildInfoWindowContent(el, ownerHtml) {
    return `
        <div class="ListMap-infowindow">
            <div class="ListMap-infowindow-name">${escapeHtml(el.NAME)}</div>
            <div>${escapeHtml(el.ADDRESS)}</div>
            <div>${escapeHtml(el.CITY)}, ${escapeHtml(el.STATE)} ${escapeHtml(el.ZIP || '')}</div>
            ${ownerHtml}
            <a href='https://iclimb.io/property/${el.ARCIMEDES_ROW_ID}' target='_blank' class="ListMap-infowindow-link">
                View Property
            </a>
        </div>
    `;
}

function renderOwnerSection(investors) {
    if (!investors || investors.length === 0) return '';
    return investors.map((inv) => {
        const name = inv.NAME || '';
        const leadId = inv.LEAD_ID;
        const ownerLink = leadId ? `https://accounts.kore.co/crm/lead/${escapeHtml(leadId)}` : null;
        const nameHtml = ownerLink
            ? `<a href="${ownerLink}" target="_blank" class="ListMap-infowindow-owner-link">${escapeHtml(name)}</a>`
            : `<strong>${escapeHtml(name)}</strong>`;
        const mobileRow = inv.MOBILE_PHONE ? `<div class="ListMap-infowindow-row">Mobile: ${escapeHtml(inv.MOBILE_PHONE)}</div>` : '';
        const workRow = inv.PHONE ? `<div class="ListMap-infowindow-row">Work: ${escapeHtml(inv.PHONE)}</div>` : '';
        const emailRow = inv.EMAIL
            ? `<div class="ListMap-infowindow-row">Email: <a href="mailto:${escapeHtml(inv.EMAIL)}">${escapeHtml(inv.EMAIL)}</a></div>`
            : '';
        const firmRow = inv.FIRM_NAME ? `<div class="ListMap-infowindow-row">${escapeHtml(inv.FIRM_NAME)}</div>` : '';
        return `
            <div class="ListMap-infowindow-owner">
                <div class="ListMap-infowindow-owner-name">${nameHtml}</div>
                ${mobileRow}
                ${workRow}
                ${emailRow}
                ${firmRow}
            </div>
        `;
    }).join('');
}

const colorMap = {};

statusTypes.forEach(el => {
	colorMap[el.code] = el.color;
});
colorMap['Removed'] = '#999';

function pinSymbol(color) {
    return {
        path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -2,-30 a 2,2 0 1,1 4,0 2,2 0 1,1 -4,0',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#333',
        strokeWeight: 1,
        scale: 1,
   };
}

class ListMap extends React.Component {
    map;
    infowindow;
    markers;

	constructor(props) {
		super(props);

        this.map = null;
        this.infowindow = null;
        this.markers = [];
	}

	componentDidMount() {
		const googleMapsApiKey = getGoogleMapsApiKey();
		if (!googleMapsApiKey) {
			console.error('Google Maps API key is not configured');
			return;
		}
		const loadClusterer = () => {
			if (window.markerClusterer) {
				this.initializeMap();
				return;
			}
			const clusterScript = document.createElement('script');
			clusterScript.src = "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
			clusterScript.async = true;
			clusterScript.onload = () => this.initializeMap();
			document.head.appendChild(clusterScript);
		};
		if (window.google && window.google.maps) {
			loadClusterer();
			return;
		}
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places`;
		script.async = true;
		script.defer = true;
		script.onload = loadClusterer;
		document.head.appendChild(script);
	}

    initializeMap() {
        this.map = new google.maps.Map(this.refs.map, {
            zoom: this.props.zoom,
            center: new google.maps.LatLng(this.props.latitude, this.props.longitude),
            mapTypeId: this.props.mapTypeId === 'satellite' ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP,
			scrollwheel: true
        });
        const listings = this._pendingListings !== undefined ? this._pendingListings : this.props.listings;
        this._pendingListings = undefined;
        this.setMarkers(listings);
    }

	openInfoWindowForProperty(marker, el) {
		if (this.infowindow) this.infowindow.close();
		const loadingHtml = `<div class="ListMap-infowindow-loading">Loading owner info…</div>`;
		this.infowindow = new google.maps.InfoWindow({
			content: buildInfoWindowContent(el, loadingHtml),
		});
		this.infowindow.open(this.map, marker);
		const openedInfoWindow = this.infowindow;

		fetchOwnerInfo(el.ARCIMEDES_ROW_ID).then((investors) => {
			if (openedInfoWindow !== this.infowindow) return; // user moved to another marker
			const ownerHtml = investors && investors.length ? renderOwnerSection(investors) : `<div class="ListMap-infowindow-owner-empty">No current owner</div>`;
			openedInfoWindow.setContent(buildInfoWindowContent(el, ownerHtml));
		});
	}

	setMarkers(listings) {
		if (!this.map) {
			this._pendingListings = listings;
			return;
		}
		if (listings) {
			this.clearMarkers();

			const clusterSvgUrl = `data:image/svg+xml;base64,${window.btoa('<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#54648C" stroke="#fff" stroke-width="3"/></svg>')}`;

			const filteredListings = listings.filter(el => {
				const lat = el.LATITUDE && el.LATITUDE.toString() || '';
				const lng = el.LONGITUDE && el.LONGITUDE.toString() || '';
				return lat && lng && !lat.startsWith('0') && !lng.startsWith('0');
			});

			// Store raw data alongside each marker — create InfoWindow lazily on first click
			const markers = filteredListings.map(el => {
				const marker = new google.maps.Marker({
					position: {
						lat: parseFloat(el.LATITUDE),
						lng: parseFloat(el.LONGITUDE)
					},
					icon: pinSymbol(colorMap[el.STATUS] || '#999')
				});

				marker.addListener('click', () => {
					this.openInfoWindowForProperty(marker, el);
				});

				return marker;
			});

			const renderer = {
				render({ count, position }) {
					return new google.maps.Marker({
						position,
						icon: {
							url: clusterSvgUrl,
							scaledSize: new google.maps.Size(40, 40),
							anchor: new google.maps.Point(20, 20),
						},
						label: {
							text: String(count),
							color: '#fff',
							fontSize: '12px',
							fontWeight: '600',
						},
						zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
					});
				}
			};

			if (window.markerClusterer && markers.length > 0) {
				this.markerCluster = new window.markerClusterer.MarkerClusterer({
					map: this.map,
					markers: markers,
					renderer,
				});
			}

			this.markers = markers;
		}
	}

	clearMarkers() {
		if (this.markerCluster) {
			this.markerCluster.clearMarkers();
		}
	
		if (this.markers) {
			this.markers.forEach(marker => marker.setMap(null));
		}
	
		this.markers = [];
	}

	appendMarkers(newListings) {
		if (!this.map || !newListings || newListings.length === 0) return;

		const clusterSvgUrl = `data:image/svg+xml;base64,${window.btoa('<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#54648C" stroke="#fff" stroke-width="3"/></svg>')}`;

		const filtered = newListings.filter(el => {
			const lat = el.LATITUDE && el.LATITUDE.toString() || '';
			const lng = el.LONGITUDE && el.LONGITUDE.toString() || '';
			return lat && lng && !lat.startsWith('0') && !lng.startsWith('0');
		});

		const newMarkers = filtered.map(el => {
			const marker = new google.maps.Marker({
				position: { lat: parseFloat(el.LATITUDE), lng: parseFloat(el.LONGITUDE) },
				icon: pinSymbol(colorMap[el.STATUS] || '#999')
			});
			marker.addListener('click', () => {
				this.openInfoWindowForProperty(marker, el);
			});
			return marker;
		});

		this.markers = this.markers.concat(newMarkers);

		if (this.markerCluster) {
			this.markerCluster.addMarkers(newMarkers);
		} else if (window.markerClusterer && this.markers.length > 0) {
			const renderer = {
				render({ count, position }) {
					return new google.maps.Marker({
						position,
						icon: {
							url: clusterSvgUrl,
							scaledSize: new google.maps.Size(40, 40),
							anchor: new google.maps.Point(20, 20),
						},
						label: {
							text: String(count),
							color: '#fff',
							fontSize: '12px',
							fontWeight: '600',
						},
						zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
					});
				}
			};
			this.markerCluster = new window.markerClusterer.MarkerClusterer({
				map: this.map,
				markers: this.markers,
				renderer,
			});
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.listings !== this.props.listings) {
			if (nextProps.appendMode && this.props.listings && nextProps.listings) {
				const newItems = nextProps.listings.slice(this.props.listings.length);
				if (newItems.length > 0) {
					this.appendMarkers(newItems);
					return;
				}
			}
			this.setMarkers(nextProps.listings);
		}
	}

	shouldComponentUpdate() {
		return false;
	}

	render() {
		return (
			<div ref='map' className='ListMap'/>
		);
	}
}

ListMap.defaultProps = {
    latitude: 32.7801399,
    longitude: -96.80045109999998,
    zoom: 4,
};

export default ListMap;